import { Button, Tag } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { StoryboardShotV1, VideoScriptStoryboardV1 } from '../../api'

function ShotCard({ shot }: { shot: StoryboardShotV1 }) {
  const { t } = useTranslation('video')
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="tc-vdc-shot-card" data-testid={`storyboard-shot-${shot.shot_id}`}>
      <div className="tc-vdc-shot-card-head">
        <span className="tc-vdc-shot-badge">SHOT {String(shot.shot_number).padStart(2, '0')}</span>
        <span className="tc-vdc-shot-time">
          {shot.start_time.toFixed(1)}s – {shot.end_time.toFixed(1)}s
        </span>
      </div>
      <div className="tc-vdc-shot-desc">{shot.visual_description}</div>
      <div className="tc-vdc-shot-tags">
        <Tag>{t(`director.shotSize.${shot.shot_size}`, { defaultValue: shot.shot_size })}</Tag>
        <Tag>{t(`director.cameraMovement.${shot.camera_movement}`, { defaultValue: shot.camera_movement })}</Tag>
        <Tag>{shot.generation_strategy}</Tag>
      </div>
      <div className="tc-vdc-shot-meta">
        <span>{t('director.storyboard.subject')}：{shot.subject_action}</span>
        <span>{t('director.storyboard.environment')}：{shot.environment}</span>
      </div>
      <Button type="link" size="small" onClick={() => setExpanded((v) => !v)}>
        {expanded ? t('director.storyboard.collapse') : t('director.storyboard.expand')}
      </Button>
      {expanded && (
        <div className="tc-vdc-shot-detail">
          <div>{t('director.storyboard.purpose')}：{shot.purpose}</div>
          <div>{t('director.storyboard.cameraPosition')}：{shot.camera_position} / {shot.camera_angle}</div>
          <div>{t('director.storyboard.composition')}：{shot.composition}</div>
          <div>{t('director.storyboard.lighting')}：{shot.lighting}</div>
          <div>{t('director.storyboard.transition')}：{shot.transition_in} → {shot.transition_out}</div>
          <div>{t('director.storyboard.continuity')}：{shot.continuity_requirements}</div>
          <div>{t('director.storyboard.audio')}：{shot.audio_cue}</div>
          <div className="tc-vdc-shot-kf">
            {(shot.keyframe_requirements || []).map((kf) => (
              <div key={`${shot.shot_id}-${kf.role}`}>
                <strong>{kf.role}</strong>：{kf.description}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default function StoryboardPanel({
  storyboard,
  selectedProposalTitle,
  selectedProposalSummary,
  status,
  storyboardStale,
  directorSelectionStale,
  errorMessage,
  confirmedBatchId,
  onGenerate,
  onRegenerate,
  onConfirm,
  disabledReason,
}: {
  storyboard: VideoScriptStoryboardV1 | null
  selectedProposalTitle: string | null
  selectedProposalSummary: string | null
  status: string
  storyboardStale: boolean
  directorSelectionStale: boolean
  errorMessage: string | null
  confirmedBatchId: string | null
  onGenerate: () => void
  onRegenerate: () => void
  onConfirm: () => void
  disabledReason: string | null
}) {
  const { t } = useTranslation('video')
  const generating = status === 'queued' || status === 'running'
  const hasStoryboard = !!storyboard?.shots?.length

  if (directorSelectionStale || !selectedProposalTitle) {
    return <div className="tc-vdc-panel-empty">{t('director.storyboard.returnToConcept')}</div>
  }

  return (
    <div className="tc-vdc-storyboard-panel">
      <div className="tc-vdc-selected-proposal-summary">
        <div className="tc-vdc-panel-sub">{t('director.storyboard.selectedProposal')}</div>
        <div className="tc-vdc-concept-title">{selectedProposalTitle}</div>
        {selectedProposalSummary && <div className="tc-vdc-shot-desc">{selectedProposalSummary}</div>}
      </div>

      {storyboardStale && <div className="tc-vdc-stale-banner">{t('director.storyboard.staleBanner')}</div>}

      {status === 'failed' && (
        <div className="tc-vdc-error-banner">{errorMessage || t('director.storyboard.generateFailed')}</div>
      )}

      {!hasStoryboard && !generating && (
        <div className="tc-vdc-panel-empty">
          <Button type="primary" onClick={onGenerate} disabled={!!disabledReason}>
            {t('director.storyboard.generateButton')}
          </Button>
          {disabledReason && <div className="tc-vdc-panel-disabled-hint">{disabledReason}</div>}
        </div>
      )}

      {generating && (
        <div className="tc-vdc-panel-empty">
          <div className="tc-vdc-panel-empty-title">{t('director.storyboard.generating')}</div>
        </div>
      )}

      {hasStoryboard && (
        <>
          <div className="tc-vdc-narrative-script">
            <div className="tc-vdc-panel-sub">{t('director.storyboard.script')}</div>
            <div className="tc-vdc-preline">{storyboard!.narrative_script}</div>
          </div>
          <div className="tc-vdc-shots-list">
            {storyboard!.shots.map((shot) => (
              <ShotCard key={shot.shot_id} shot={shot} />
            ))}
          </div>
          <div className="tc-vdc-panel-regen-row">
            <Button onClick={onRegenerate} disabled={generating}>
              {t('director.storyboard.regenerate')}
            </Button>
            <Button type="primary" onClick={onConfirm} disabled={generating || storyboardStale || !!confirmedBatchId}>
              {confirmedBatchId ? t('director.storyboard.confirmed') : t('director.storyboard.confirm')}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
